/**
 * Detects messages that contain database internals (SQL fragments,
 * constraint violations, etc.) which should never be shown to users.
 */
function looksLikeInternalError(message: string): boolean {
  const lower = message.toLowerCase()
  return (
    /failed query:/i.test(message) ||
    /\bselect\b.*\bfrom\b/i.test(message) ||
    /\binsert\b.*\binto\b/i.test(message) ||
    lower.includes('duplicate key value') ||
    lower.includes('violates') ||
    lower.includes('relation "') ||
    lower.includes('column "') ||
    lower.includes('params:')
  )
}

function normalizeApiMessage(
  value: string | Array<string> | undefined,
): string | undefined {
  if (value === undefined) {
    return undefined
  }

  if (Array.isArray(value)) {
    const first = value.find(
      (item) => typeof item === 'string' && item.trim().length > 0,
    )
    return first ?? value.join(', ')
  }

  return value
}

/**
 * Human-readable API error string for UI (axios-style errors + plain Error).
 *
 * Only surfaces messages that originate from backend API responses.
 * Internal JS errors (missing version, null refs, etc.) are replaced
 * with the caller-provided fallback so users never see developer jargon.
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  if (!error || typeof error !== 'object') return fallback

  const maybeError = error as {
    message?: string | Array<string>
    response?: {
      data?: {
        message?: string | Array<string>
        error?: string | Array<string>
        userAction?: string | Array<string>
      }
    }
  }

  const responseData = maybeError.response?.data
  const userAction = normalizeApiMessage(responseData?.userAction)
  const message = normalizeApiMessage(responseData?.message)
  const errorField = normalizeApiMessage(responseData?.error)

  if (userAction && message) {
    const combined = `${message} ${userAction}`
    if (looksLikeInternalError(combined)) return fallback
    return combined
  }

  if (message) {
    if (looksLikeInternalError(message)) return fallback
    return message
  }

  if (errorField) {
    if (looksLikeInternalError(errorField)) return fallback
    return errorField
  }

  return fallback
}
